import { Range } from 'domains/reporter/RichTextEditor/core';
import type { OnExternalSubstitution } from '../types';
import type {
  ErrorResponse,
  HypothesisTextResponse,
  StableTextResponse,
  FocusMapAppendResponse,
  FocusMapDeleteResponse,
  CommandResponse,
  HangStudyCommandResponse,
  UpdateToolInteractionCommandResponse,
  SDKCommandResponse,
  ReportGeneratedResponse,
  CursorContext,
} from './ASRPlexProtocol';
import type { ReportStatuses } from 'domains/reporter/Reporter/state';
import type {
  RecorderEventPayloadMap,
  RecorderModifyDictationEvent,
  RecorderStopRecordingEvent,
} from 'common/Recorder/useRecorder/useRecorder';
import {
  isPCMRecorderDataAvailableEvent,
  useRecorder,
} from 'common/Recorder/useRecorder/useRecorder';

import { getEditorDictationSelectionSafe } from '../../../utils';
import { getCursorContext } from '../../../utils/getCursorContext';
import { PROVISIONAL_SUBMIT_SELECTION } from '../NvoqQueue/useNvoqQueue';
import { DictationStableTextManagerMapValue, useTextProcessing } from '../useTextProcessing';
import { ASRPlexTransaction } from './ASRPlexProtocol';

import { recorderEmitter } from 'common/Recorder/useRecorder';
import { convertUnifiedToFields } from 'domains/reporter/Reporter/Fields';
import {
  IN_FLIGHT_PRELIM_STATUSES,
  IN_FLIGHT_STATUSES,
  PROVISIONAL_SUBMIT_STATUSES,
  reportStatusState,
} from 'domains/reporter/Reporter/state';
import {
  triggerToolInteractionResponse,
  useSDKPayload,
} from 'domains/viewer/ViewportDre/AnnotationTools/ConfigBasedTool/clientSDK';
import { useCaseSync } from 'hooks/useCaseSync';
import { useCurrentCaseId, useCurrentComparativeStudies } from 'hooks/useCurrentCase';
import { logger } from 'modules/logger';
import { useCallback, useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Editor } from 'slate';
import { useFocusMode } from 'hooks/useFocusMode';
import { useReportTemplate } from 'hooks/useReportTemplate';
import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { NvoqMessage } from '../NvoqQueue/createNvoqWebsocketFactory';
import { useRetryASRPlexWebSocket } from './useRetryASRPlexWebSocket';
import { asrPlexWebSocketState } from './state';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { telemetryContextFor } from 'modules/Apollo/types';

// Maps the camelCase cursor context computed from the editor to the snake_case payload contract
// expected by the ASR Plex backend. Returns undefined when no selection is available so the field
// is omitted from the request rather than sent as null.
const buildCursorContextPayload = (
  editor: Editor,
  selection: Range | null
): CursorContext | undefined => {
  if (selection == null) {
    return undefined;
  }

  const { sentenceBefore, sentenceAfter, selectedText } = getCursorContext(editor, selection);

  return {
    sentence_before: sentenceBefore,
    sentence_after: sentenceAfter,
    selected_text: selectedText,
  };
};

export const asrPlexConfiguration = {
  id: 'asr-plex-default-2023-11-30',
  label: 'ASR Plex Default',
  audioSamplingRate: 48000,
  webmEnabled: false,
  leadingSilenceDetectionEnabled: false,
  echoCancellation: true,
  autoGainControl: true,
  noiseSuppression: true,
} as const;

export type ASRPlexDictationProps = {
  editor: Editor;
  isEnabled: boolean;
  isAlwaysEditable?: boolean;
  getActiveTemplateId?: () => string | undefined;
  onWebsocketError: () => void;
  onError: (e: ErrorResponse) => void;
  enablePicklistDictation: boolean;
  onExternalSubstitution: OnExternalSubstitution;
  children: React.ReactNode;
  transactionIdToTransactionMapRef?: React.MutableRefObject<Map<string, ASRPlexTransaction>>;
  dictationStableTextManagerMapRef?: React.MutableRefObject<
    Map<string, DictationStableTextManagerMapValue>
  >;
};

let activeASRPlexDictationInstances = 0;

type PendingModifyDictationEvent = RecorderModifyDictationEvent & {
  selection: Range | null;
  selectionResolved: boolean;
};

export function ASRPlexDictation({
  editor,
  isEnabled,
  isAlwaysEditable,
  getActiveTemplateId,
  onWebsocketError,
  onError,
  enablePicklistDictation,
  onExternalSubstitution,
  transactionIdToTransactionMapRef,
  dictationStableTextManagerMapRef,
  children,
}: ASRPlexDictationProps): React.ReactElement {
  const reportStatus = useRecoilValue<ReportStatuses>(reportStatusState);
  const internalTransactionMapRef = useRef<Map<string, ASRPlexTransaction>>(new Map());
  // Keep finalized IDs for this provider's lifetime so late recorder events remain guarded across
  // report changes. Keep them separate from the active dictation map so normal cleanup is unchanged.
  const finalizedDictationIdsRef = useRef(new Set<string>());
  // transactionIdToTransactionMapRef is optional for use in tests, otherwise it will use an internal ref
  const transactionIdToTransactionMap =
    transactionIdToTransactionMapRef ?? internalTransactionMapRef;
  const pendingModifyDictationEventsRef = useRef<Map<string, PendingModifyDictationEvent[]>>(
    new Map()
  );
  const setSocketState = useSetRecoilState(asrPlexWebSocketState);
  const caseSmid = useCurrentCaseId();
  const { isFocusModeOn } = useFocusMode({ context: APOLLO_QUERY_CONTEXT });
  const [isReporterModifyDictationEnabled] = useFeatureFlagEnabled(FF.REPORTER_MODIFY_DICTATION);
  const { template } = useReportTemplate({ context: APOLLO_QUERY_CONTEXT });
  const { currentCaseReport } = useCurrentCaseReport();
  const { restartRecording } = useRecorder();

  useEffect(() => {
    activeASRPlexDictationInstances += 1;
    if (activeASRPlexDictationInstances > 1) {
      logger.warn('[ASRPlexDictation] Multiple ASRPlexDictation instances mounted concurrently.', {
        activeInstances: activeASRPlexDictationInstances,
        caseSmid,
      });
    }
    return () => {
      activeASRPlexDictationInstances = Math.max(0, activeASRPlexDictationInstances - 1);
    };
  }, [caseSmid]);

  useEffect(() => {
    // When recording is stopped, stop all open transactions
    // forcing them to be resolved
    if (!isEnabled) {
      const transactions = transactionIdToTransactionMap.current.values();

      for (const transaction of transactions) {
        if (transaction.state !== 'stopped') {
          transaction.stopTransaction();
        }
      }
    }
  }, [isEnabled, transactionIdToTransactionMap]);

  const [studySmids] = useCurrentComparativeStudies({ context: APOLLO_QUERY_CONTEXT });
  const syncCase = useCaseSync({ context: APOLLO_QUERY_CONTEXT });
  const sdkVoiceCommandPayload = useSDKPayload({ context: APOLLO_QUERY_CONTEXT });
  const socket = useRetryASRPlexWebSocket();
  const markDictationsFinalized = useCallback((ids: string[]) => {
    ids.forEach((id) => finalizedDictationIdsRef.current.add(id));
  }, []);
  const {
    processStableTextSerially,
    processFocusMapAppend,
    processFocusMapDelete,
    processHypothesisText,
    setShouldSkipWebsocketCreationRef,
    skipWebsocketCreationOptionsRef,
    registerDictation,
    flushDeferredStepsForSelection,
    markDictationStopped,
    // This is only passed through to the dictationStableTextManagerMapRef for testing purposes.
    dictationStableTextManagerMap,
  } = useTextProcessing({
    editor,
    enablePicklistDictation,
    onExternalSubstitution,
    context: APOLLO_QUERY_CONTEXT,
    isAlwaysEditable,
    onDictationFinalized: markDictationsFinalized,
  });

  if (dictationStableTextManagerMapRef) {
    dictationStableTextManagerMapRef.current = dictationStableTextManagerMap.current;
  }

  const stopTransaction = useCallback(
    ({ id, selection }: { selection: Range | null; id: string }) => {
      if (selection == null) {
        // handle error case
        return;
      }

      const transaction = transactionIdToTransactionMap.current.get(id);
      if (transaction == null) {
        // handle error case
        return;
      }
      if (transaction.state === 'stopped') {
        return;
      }
      transaction.stopTransaction();
    },
    [transactionIdToTransactionMap]
  );

  const stopAllTransactions = useCallback(() => {
    // Record transaction IDs before clearing the map so an error cannot recreate stopped work.
    for (const id of transactionIdToTransactionMap.current.keys()) {
      finalizedDictationIdsRef.current.add(id);
    }

    for (const [id, transaction] of transactionIdToTransactionMap.current.entries()) {
      const selection = transaction?.selection;
      if (selection != null) {
        stopTransaction({ selection, id });
      }
    }
    transactionIdToTransactionMap.current.clear();

    for (const entry of dictationStableTextManagerMap.current.values()) {
      const dictationEntry = entry as Partial<DictationStableTextManagerMapValue>;
      dictationEntry.textDone = true;
      dictationEntry.deferred?.resolve();
      dictationEntry.messageQueue?.forEach((deferred) => deferred.resolve());
    }
    dictationStableTextManagerMap.current.clear();
    pendingModifyDictationEventsRef.current.clear();
  }, [dictationStableTextManagerMap, stopTransaction, transactionIdToTransactionMap]);

  const registerModifyDictationSelection = useCallback(
    (
      transaction: ASRPlexTransaction,
      { id, audioOffsetMs, localSequence }: RecorderModifyDictationEvent,
      selectionForDictation: Range
    ) => {
      const previousSelection = transaction.selection;

      if (previousSelection != null && !Range.equals(previousSelection, selectionForDictation)) {
        flushDeferredStepsForSelection(previousSelection, id);
      }

      transaction.selection = selectionForDictation;

      registerDictation(id, {
        selection: selectionForDictation,
        audioOffsetMs,
        localSequence,
      });
    },
    [flushDeferredStepsForSelection, registerDictation]
  );

  const applyPendingModifyDictationEvents = useCallback(
    (id: string, transaction: ASRPlexTransaction) => {
      const pendingEvents = pendingModifyDictationEventsRef.current.get(id);
      if (pendingEvents == null || pendingEvents.length === 0) {
        return;
      }

      while (pendingEvents[0]?.selectionResolved) {
        const pendingEvent = pendingEvents.shift();
        if (pendingEvent == null) {
          break;
        }

        logger.info(
          '[ASRPlexDictation] Processing queued modify dictation after transaction creation',
          {
            id,
            audioOffsetMs: pendingEvent.audioOffsetMs,
            localSequence: pendingEvent.localSequence,
          }
        );

        transaction.modifyDictation(pendingEvent.audioOffsetMs, {
          localSequence: pendingEvent.localSequence,
          cursor_context: buildCursorContextPayload(editor, pendingEvent.selection),
        });

        if (pendingEvent.selection == null) {
          logger.warn(
            '[ASRPlexDictation] Skipping queued modify dictation registration because selection is null',
            { id }
          );
          continue;
        }

        registerModifyDictationSelection(transaction, pendingEvent, pendingEvent.selection);
      }

      if (pendingEvents.length === 0) {
        pendingModifyDictationEventsRef.current.delete(id);
      }
    },
    [editor, registerModifyDictationSelection]
  );

  const queuePendingModifyDictationEvent = useCallback(
    (event: RecorderModifyDictationEvent) => {
      const pendingEvent: PendingModifyDictationEvent = {
        ...event,
        selection: null,
        selectionResolved: false,
      };
      const pendingEvents = pendingModifyDictationEventsRef.current.get(event.id) ?? [];
      pendingEvents.push(pendingEvent);
      pendingModifyDictationEventsRef.current.set(event.id, pendingEvents);
      logger.info('[ASRPlexDictation] Queued modify dictation until transaction is created', {
        id: event.id,
        audioOffsetMs: event.audioOffsetMs,
        localSequence: event.localSequence,
        pendingEventCount: pendingEvents.length,
      });

      // Selection changes caused by keyboard navigation may not be visible until the
      // next frame, even when the modify event itself arrives before the transaction.
      requestAnimationFrame(() => {
        try {
          pendingEvent.selection = getEditorDictationSelectionSafe(editor);
        } catch (error) {
          logger.warn(
            '[ASRPlexDictation] Failed to resolve selection for queued modify dictation registration',
            { id: event.id, error }
          );
        } finally {
          pendingEvent.selectionResolved = true;
        }

        const transaction = transactionIdToTransactionMap.current.get(event.id);
        if (transaction != null) {
          applyPendingModifyDictationEvents(event.id, transaction);
        }
      });
    },
    [applyPendingModifyDictationEvents, editor, transactionIdToTransactionMap]
  );

  const createNewTransaction = useCallback(
    ({ socket, id, selection }: { socket: WebSocket; id: string; selection: Range }) => {
      const transaction = new ASRPlexTransaction(
        id,
        socket,
        isFocusModeOn,
        isReporterModifyDictationEnabled
      );
      const templateId = getActiveTemplateId?.();

      transaction.startTransaction({
        case_smid: caseSmid ?? undefined,
        template_smid: templateId ?? undefined,
        report_selection: selection,
        report_content: editor.children,
        report_fields: convertUnifiedToFields(editor.children),
        focus_mode: isFocusModeOn,
        modify_mode: isReporterModifyDictationEnabled,
        sdk_context: sdkVoiceCommandPayload ?? undefined,
        cursor_context: buildCursorContextPayload(editor, selection),
        template_fields: template?.sections
          ? [...template.sections]
          : currentCaseReport?.report?.template?.sections
            ? [...currentCaseReport.report.template.sections]
            : undefined,
      });

      const transaction_id = transaction.transaction_id;
      setSocketState((prev) => ({
        ...prev,
        active_transaction_id: transaction_id,
      }));
      transactionIdToTransactionMap.current.set(transaction.transaction_id, transaction);

      // Create a range ref that will keep the user's intended target for this dictation
      // in sync with changes to the document.
      registerDictation(transaction_id, { selection });
      // Replay queued target changes before stable text can be applied to the initial selection.
      applyPendingModifyDictationEvents(transaction_id, transaction);
      return transaction;
    },
    [
      getActiveTemplateId,
      caseSmid,
      template?.sections,
      editor,
      isFocusModeOn,
      sdkVoiceCommandPayload,
      setSocketState,
      currentCaseReport?.report?.template?.sections,
      registerDictation,
      applyPendingModifyDictationEvents,
      transactionIdToTransactionMap,
      isReporterModifyDictationEnabled,
    ]
  );

  useEffect(() => {
    if (socket == null) return;

    const onMessage = async (ev: MessageEvent) => {
      const msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as
        | HypothesisTextResponse
        | StableTextResponse
        | FocusMapAppendResponse
        | FocusMapDeleteResponse
        | CommandResponse
        | SDKCommandResponse
        | ReportGeneratedResponse
        | ErrorResponse;

      if (msg.type === 'hypothesis_text') {
        const { transaction_id, text } = msg.payload;

        if (isFocusModeOn) return;

        const transaction = transactionIdToTransactionMap.current.get(transaction_id);
        const selection = transaction?.selection;

        if (selection == null) {
          // handle error case
          return;
        }

        // Simulate an NvoqMessage, since this is expected downstream.
        // TODO: Flatten signature and expect more primitive types.
        const nvoqMessage: NvoqMessage = {
          id: '',
          apiVersion: '',
          method: 'TEXT',
          data: {
            text,
            substitutedText: text,
            kind: 'HYPOTHESISTEXT',
            markers: [],
            textDone: false,
            maxAlternates: 0,
          },
        };
        processHypothesisText(nvoqMessage, selection, transaction_id);
      } else if (msg.type === 'stable_text') {
        const { markers, transaction_id, text, done } = msg.payload;

        const transaction = transactionIdToTransactionMap.current.get(transaction_id);
        if (transaction == null) {
          logger.warn('[ASRPlexDictation] Stable text received without transaction', {
            transaction_id,
            done,
            textLength: text.length,
            markers: markers ?? [],
            knownTransactions: Array.from(transactionIdToTransactionMap.current.keys()),
          });
          return;
        }

        const selection = transaction.selection;

        if (selection == null) {
          logger.warn('[ASRPlexDictation] Stable text received without selection', {
            transaction_id,
            done,
            textLength: text.length,
            markers: markers ?? [],
            transactionState: transaction.state,
            knownTransactions: Array.from(transactionIdToTransactionMap.current.keys()),
          });
          return;
        }

        logger.info(`[ASRPlexDictation] Stable text received: "${text}"`, {
          transaction_id,
          done,
          textLength: text.length,
          markerCount: markers?.length ?? 0,
          markers: markers ?? [],
        });

        // Simulate an NvoqMessage, since this is expected downstream.
        // TODO: Flatten signature and expect more primitive types.
        const nvoqMessage = {
          id: transaction_id,
          apiVersion: '',
          method: 'TEXT',
          data: {
            text,
            substitutedText: text,
            kind: 'STABLETEXT',
            markers,
            textDone: done,
            maxAlternates: 0,
          },
        } as NvoqMessage;

        await processStableTextSerially(nvoqMessage, selection, transaction_id);

        if (done) {
          transactionIdToTransactionMap.current.delete(transaction_id);
          pendingModifyDictationEventsRef.current.delete(transaction_id);
        }
      } else if (msg.type === 'focus_map_delete') {
        const { text, section } = msg.payload;
        const isEmptyText = text.trim() === '';

        logger.info(`[ASRPlexDictation] Focus map delete received: "${text}"`, {
          section,
          sectionDepth: section.length,
          textLength: text.length,
          isEmptyText,
        });

        if (isEmptyText) {
          logger.info('[ASRPlexDictation] Focus map delete ignored because text is empty', {
            section,
          });
          return;
        }

        processFocusMapDelete(text, section);
      } else if (msg.type === 'focus_map_append') {
        const { text, section } = msg.payload;
        const isEmptyText = text.trim() === '';

        logger.info(`[ASRPlexDictation] Focus map append received: "${text}"`, {
          section,
          sectionDepth: section.length,
          textLength: text.length,
          isEmptyText,
        });

        // USE THE COMMENTED CODE 2 LINES BELOW IF YOU WANT TO MOCK IT
        // const { text } = msg.payload;
        // const section = ['findings'];

        // shortcut: were turning it into nVoq stable text, so that we can create 'steps' in the
        // same way, even though its not a message from nVoq.
        const nvoqMessage: NvoqMessage = {
          id: '',
          apiVersion: '',
          method: 'TEXT',
          data: {
            text,
            substitutedText: text,
            kind: 'STABLETEXT',
            markers: [],
            textDone: false,
            maxAlternates: 0,
          },
        };

        if (isEmptyText) {
          logger.info('[ASRPlexDictation] Focus map append ignored because text is empty', {
            section,
          });
          return;
        }

        processFocusMapAppend(nvoqMessage, section);
      } else if (msg.type === 'command') {
        if (msg.payload.name === 'hang_study_by_id') {
          const { smid } = (msg as HangStudyCommandResponse).payload.arguments;
          if (caseSmid != null) {
            syncCase(caseSmid, [...studySmids, smid]);
          }
        } else if (msg.payload.name === 'update_tool_interaction') {
          const { llm_response_json } = (msg as UpdateToolInteractionCommandResponse).payload
            .arguments;
          const llmResponseJsonList = JSON.parse(llm_response_json);
          triggerToolInteractionResponse(llmResponseJsonList);
        }
      } else if (msg.type === 'error') {
        onError(msg);

        stopAllTransactions();
      } else {
        // TODO handle error case
      }
    };
    socket.addEventListener('message', onMessage);

    const onSocketClose = () => {
      logger.info('[ASRPlexDictation] WebSocket closed; marking active dictations stopped', {
        transactionCount: transactionIdToTransactionMap.current.size,
      });
      // Do not stop or clear transactions here: the recorder/retry path may still recover.
      // We only mark active dictation entries stopped so stable-text ordering can later
      // evict an orphaned blocker after its grace window instead of waiting forever.
      for (const id of transactionIdToTransactionMap.current.keys()) {
        markDictationStopped(id);
      }
    };
    socket.addEventListener('close', onSocketClose);

    const onProcessedDataAvailable = (event: RecorderEventPayloadMap['data_available']): void => {
      if (!isPCMRecorderDataAvailableEvent(event)) {
        return;
      }

      const {
        data: blob,
        buffer,
        initialSelectionRef: recorderSelectionRef,
        id,
        duration,
        frameCount,
        setShouldSkipWebsocketCreation,
        shouldSkipWebsocketCreation,
      } = event;
      let selection: Range | null;

      if (
        PROVISIONAL_SUBMIT_STATUSES.includes(
          reportStatus as (typeof PROVISIONAL_SUBMIT_STATUSES)[number]
        )
      ) {
        selection = PROVISIONAL_SUBMIT_SELECTION;
      } else {
        selection = recorderSelectionRef?.current ?? editor.selection;
      }
      setShouldSkipWebsocketCreationRef.current = setShouldSkipWebsocketCreation;
      skipWebsocketCreationOptionsRef.current = {
        // for whether hypothesis text selection should use this selection or native from event
        shouldSkipWebsocketCreation,
      };

      const packetMetadata = {
        transaction_id: id,
        recorder_duration_ms: duration,
        frame_count: frameCount,
        buffer_sample_count: buffer.length,
        buffer_byte_length: buffer.byteLength,
        blob_size_bytes: blob.size,
        should_skip_websocket_creation: shouldSkipWebsocketCreation,
        has_selection: selection != null,
        report_status: reportStatus,
        active_transaction_count: transactionIdToTransactionMap.current.size,
      };

      if (finalizedDictationIdsRef.current.has(id)) {
        logger.warn(
          '[ASRPlexDictation] Ignoring stale audio for finalized dictation',
          packetMetadata
        );
        return;
      }

      if (!isEnabled) {
        logger.info(
          '[ASRPlexDictation] Ignoring recorder data while dictation is disabled',
          packetMetadata
        );
        return;
      }

      if (!selection || buffer == null || buffer.length === 0) {
        logger.info('[ASRPlexDictation] onProcessedDataAvailable: dropping audio packet', {
          hasSelection: selection != null,
          hasBuffer: buffer != null && buffer.length > 0,
          reportStatus,
          id,
        });
        return;
      }
      if (
        !isAlwaysEditable &&
        (IN_FLIGHT_STATUSES.includes(reportStatus as (typeof IN_FLIGHT_STATUSES)[number]) ||
          IN_FLIGHT_PRELIM_STATUSES.includes(
            reportStatus as (typeof IN_FLIGHT_PRELIM_STATUSES)[number]
          ))
      ) {
        logger.info('[ASRPlexDictation] Ignoring audio packet while report status is in-flight', {
          transaction_id: id,
          reportStatus,
        });
        return;
      }

      let transaction = transactionIdToTransactionMap.current.get(id);
      if (transaction == null) {
        transaction = createNewTransaction({
          socket,
          selection,
          id,
        });
      }
      if (transaction.state !== 'stopped') {
        const didSend = transaction.sendAudio(buffer);

        if (didSend === false) {
          logger.warn(
            '[ASRPlexDictation] WebSocket connection failed whilst sending audio. Stopping transaction and clearing mapping.',
            {
              transaction_id: id,
            }
          );

          stopAllTransactions();

          restartRecording();
        }
      }
    };

    recorderEmitter.on('data_available', onProcessedDataAvailable);

    const onModifyDictation = ({
      id,
      audioOffsetMs,
      localSequence,
    }: RecorderModifyDictationEvent) => {
      const transaction = transactionIdToTransactionMap.current.get(id);

      if (transaction == null) {
        queuePendingModifyDictationEvent({ id, audioOffsetMs, localSequence });
        return;
      }

      // The cursor context is derived from the current editor selection at send time. The fresh
      // post-navigation selection used for registration below is not yet readable synchronously,
      // but the modify message must be sent in order with the audio stream, so it cannot wait for
      // the next frame.
      transaction.modifyDictation(audioOffsetMs, {
        localSequence,
        cursor_context: buildCursorContextPayload(editor, editor.selection),
      });

      // A requestAnimationFrame is needed here to allow Slate to update its selection state
      // before we attempt to read it. Without this, we may read stale selection state.
      // Especially for keyboard inputs, doesn't seem to apply to gamepad inputs.
      requestAnimationFrame(() => {
        if (editor.selection == null) {
          logger.warn(
            '[ASRPlexDictation] Skipping modify dictation registration because selection is null',
            { id }
          );
          return;
        }

        try {
          const selectionForDictation = getEditorDictationSelectionSafe(editor);
          registerModifyDictationSelection(
            transaction,
            { id, audioOffsetMs, localSequence },
            selectionForDictation
          );
        } catch (error) {
          logger.warn(
            '[ASRPlexDictation] Failed to resolve selection for modify dictation registration',
            { id, error }
          );
        }
      });
    };

    recorderEmitter.on('modify_dictation', onModifyDictation);

    const onStopRecording = ({ selection: recorderSelection, id }: RecorderStopRecordingEvent) => {
      let selection: Range | null;

      if (
        PROVISIONAL_SUBMIT_STATUSES.includes(
          reportStatus as (typeof PROVISIONAL_SUBMIT_STATUSES)[number]
        )
      ) {
        selection = PROVISIONAL_SUBMIT_SELECTION;
      } else {
        selection = recorderSelection ?? editor.selection;
      }

      markDictationStopped(id);
      if (!transactionIdToTransactionMap.current.has(id)) {
        pendingModifyDictationEventsRef.current.delete(id);
      }
      stopTransaction({ selection, id });
    };

    recorderEmitter.on('stop_recording', onStopRecording);

    return () => {
      socket.removeEventListener('message', onMessage);
      socket.removeEventListener('close', onSocketClose);
      recorderEmitter.off('data_available', onProcessedDataAvailable);
      recorderEmitter.off('stop_recording', onStopRecording);
      recorderEmitter.off('modify_dictation', onModifyDictation);
    };
  }, [
    setSocketState,
    socket,
    caseSmid,
    editor.selection,
    isEnabled,
    reportStatus,
    onError,
    studySmids,
    syncCase,
    processStableTextSerially,
    processHypothesisText,
    setShouldSkipWebsocketCreationRef,
    skipWebsocketCreationOptionsRef,
    isFocusModeOn,
    createNewTransaction,
    stopTransaction,
    processFocusMapAppend,
    processFocusMapDelete,
    transactionIdToTransactionMap,
    dictationStableTextManagerMap,
    stopAllTransactions,
    registerDictation,
    markDictationStopped,
    restartRecording,
    registerModifyDictationSelection,
    queuePendingModifyDictationEvent,
    editor,
    isAlwaysEditable,
    finalizedDictationIdsRef,
  ]);
  return <>{children}</>;
}

ASRPlexDictation.displayName = 'ASRPlexDictation';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ASRPlexDictation);
